/** THE REFUSAL (Tom, 2026-10-10: every refused landing — commit, accept,
 *  merge — fails whole, and ONE notice says what failed and why).
 *
 *  Every versioning route answers `{code, reason, failed[], next[], …extras}`
 *  (VC-D's refusal set 1, 2026-10-14; VC-Authz's refused turns carry it
 *  too). `toRefusal` reads that, and the few other shapes a route still
 *  sends, into one shape, so a page has one thing to show however the
 *  refusal arrived:
 *
 *  - the uniform body itself (`code` + `reason`), e.g. `changed_since_review`;
 *  - the commit gate's 403 `{code: "commit_refused", what, refused, hidden}`;
 *  - an overlay commit refused because my changes were re-based (409
 *    `{rebased: true, overrides}`): review again, never commit unseen;
 *  - a lane or merge block (409 `{reason, conflicts…}`);
 *  - the server's own sentence (a string `detail`, e.g. a 413 "too large");
 *  - anything else: the error's own message.
 *
 *  A record the reader may not see is COUNTED (`hidden`), never named. */

import { ApiError } from "./api-error";
import { NOTED_WIRE, notedIn, type NotedConflict } from "./pins/noted-conflicts";

export type RefusalItem = {
  /** what failed, in words ("delete Requirement “R-12”") */
  what: string;
  /** why, when the server says */
  why?: string;
  /** the record it is about, for a page that may know its name (never
   *  shown itself): an undo's link held by `id` now, or `id` gone */
  about?: { id: string; as: "link_of" | "record" };
};

export type Refusal = {
  invalidate_review?: boolean;
  code: string;
  /** one plain sentence: what happened */
  reason: string;
  failed: RefusalItem[];
  /** failed items the reader may not see: counted, not named */
  hidden?: number;
  /** failed items past those listed (the server lists at most 50) */
  more?: number;
  /** what the reader can do next, as action names (`reload`,
   *  `switch_organisation`, `review_again`, …); a page offers those it can */
  next: string[];
  /** `other_organisation`: where the thing lives */
  organisation?: { id: string; name?: string };
  /** the HTTP status, when it came from a response */
  status?: number;
  /** `internal_error`: the server's log reference, to quote when reporting
   *  it (the reason is generic: the server never sends its error's text) */
  ref?: string;
  /** `island_in_use` (VC-D R1): the islands that pin what is deleted, by
   *  name — only those the reader may read (at most 5) */
  holders?: RefusalIsland[];
  /** `cascade_crosses_island`, `island_retired`: the islands it concerns;
   *  an unnamed one is not listed */
  islands?: RefusalIsland[];
  /** islands past those listed, or that the reader may not see: counted */
  others?: number;
  /** `other_island` (V26: a chat edits only its own project, on main, a
   *  draft or a branch): the island to open to make the change there —
   *  sent only when the reader may read it, with `next: ["open_island"]` */
  open?: RefusalIsland;
  /** V33: commit, accept or merge refused while conflicts are only noted —
   *  each one, to align or keep as an override */
  noted?: NotedConflict[];
  /** `pin_links_broken` (VC-D): a commit, accept or merge that would land
   *  links into records the moved pin's version lacks — those records, by
   *  name where readable (the rest counted in `hiddenRecords`) */
  records?: string[];
  hiddenRecords?: number;
};
export type RefusalIsland = { id: string; name?: string; type?: string };

const islandsOf = (v: unknown): RefusalIsland[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x) => !!x && typeof x === "object" && typeof x.id === "string" && x.id)
    .map((x) => ({ id: x.id as string, ...(str(x.name) ? { name: x.name as string } : {}), ...(str(x.type) ? { type: x.type as string } : {}) }));
  return out.length ? out : undefined;
};

const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);

/** One `failed[]` entry, whatever its fields are called. */
/** "NumericAttribute" → "numeric attribute" */
const typeWords = (t: string | undefined) => t && t.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();

/** An identifier, not words: a uuid or a long hex id. */
const bareId = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) || /^[0-9a-f]{24,}$/i.test(s);

/** A failed entry in words, or none: an entry that is only an id is never
 *  listed (VC-core, 2026-10-20: V26's other_island named its island by id). */
function item(raw: unknown): RefusalItem | null {
  if (typeof raw === "string") return bareId(raw.trim()) ? null : { what: raw };
  const r = (raw ?? {}) as Record<string, unknown>;
  const named = [str(r.action), str(r.class) ?? typeWords(str(r.entity_type) ?? str(r.type)), str(r.name) ? `“${r.name}”` : undefined].filter(Boolean).join(" ");
  const what = str(r.what) ?? str(r.label) ?? str(r.title) ?? (named || undefined);
  const id = str(r.id) ?? str(r.entity_id);
  // an undo's confirm refused `conflicts` (vc-impl A4): a link whose place
  // another record holds now ("<that record's id>:<link type>"), or a
  // record gone since the review
  const link = str(r.link);
  if (!what && link && link.lastIndexOf(":") > 0) {
    const at = link.lastIndexOf(":");
    return { what: `the “${typeWords(link.slice(at + 1))}” link of a record`, why: "it holds another record now", about: { id: link.slice(0, at), as: "link_of" } };
  }
  const gone = str(r.record_id);
  if (!what && gone) return { what: "a record", why: "it is gone now", about: { id: gone, as: "record" } };
  if (!what && id && bareId(id)) return null;
  return { what: what ?? id ?? "a change", why: str(r.why) ?? str(r.reason) ?? str(r.detail) };
}

const items = (list: unknown[]): RefusalItem[] => list.map(item).filter((x): x is RefusalItem => !!x);

function fromDetail(detail: unknown, status: number | undefined): Refusal | null {
  if (typeof detail === "string") return str(detail) ? { code: "refused", reason: detail, failed: [], next: [], status } : null;
  if (!detail || typeof detail !== "object") return null;
  const d = detail as Record<string, any>;
  const next = Array.isArray(d.next) ? d.next.filter((n: unknown): n is string => typeof n === "string") : [];
  const hidden = typeof d.hidden === "number" && d.hidden > 0 ? d.hidden : undefined;

  if (d.code === "commit_refused") {
    const what = str(d.what) ?? "land";
    // the shape's `failed` (the first 50, `failed_total` past them), else the
    // gate's own `refused`; `hidden` is its own count, never in the total
    const listed: unknown[] = Array.isArray(d.failed) && d.failed.length ? d.failed : Array.isArray(d.refused) ? d.refused : [];
    const failed = items(listed);
    const total = typeof d.failed_total === "number" ? d.failed_total : failed.length;
    return {
      code: "commit_refused",
      reason: `Nothing was saved: you may not ${what} ${total + (hidden ?? 0) === 1 ? "this change" : "these changes"}.`,
      failed, hidden, next, status,
      ...(total > failed.length ? { more: total - failed.length } : {}),
    };
  }
  if (d.rebased === true) {
    const overrides = Array.isArray(d.overrides) ? d.overrides : [];
    return {
      code: "rebased",
      reason: overrides.length
        ? "Nothing was committed: some of your changes were also changed by someone else since you started. Review them, then commit again."
        : "Nothing was committed: your changes were brought up to date while you were looking. Review them, then commit again.",
      failed: items(overrides), next: next.length ? next : ["review_again"], status,
    };
  }
  if (str(d.code) && str(d.reason)) {
    // an entry about a record the reader may not see is counted, never named
    // (`{action, hidden: true, why}`); past the listed ones, `failed_total`
    const entries: unknown[] = Array.isArray(d.failed) ? d.failed : [];
    const unseen = entries.filter((f) => (f as { hidden?: unknown })?.hidden === true);
    const shown = entries.filter((f) => (f as { hidden?: unknown })?.hidden !== true);
    const total = typeof d.failed_total === "number" ? d.failed_total : entries.length;
    const counted = (hidden ?? 0) + unseen.length;
    return {
      code: d.code, reason: d.reason, failed: items(shown), hidden: counted || undefined,
      ...(total > entries.length ? { more: total - entries.length } : {}),
      next: d.invalidate_review === true && !next.includes("review_again") ? [...next, "review_again"] : next,
      ...(d.invalidate_review === true ? { invalidate_review: true } : {}),
      ...(d.organisation && typeof d.organisation === "object" && str(d.organisation.id)
        ? { organisation: { id: d.organisation.id, name: str(d.organisation.name) } }
        : {}),
      ...(str(d.ref) ? { ref: d.ref } : {}),
      ...(islandsOf(d.holders) ? { holders: islandsOf(d.holders) } : {}),
      ...(islandsOf(d.islands) ? { islands: islandsOf(d.islands) } : {}),
      ...(typeof d.others === "number" && d.others > 0 ? { others: d.others } : {}),
      // the island to open, when the server offers it (and only then)
      ...(next.includes("open_island") && islandsOf(d.failed)?.find((i) => i.type) ? { open: islandsOf(d.failed)!.find((i) => i.type) } : {}),
      ...(notedIn(d, NOTED_WIRE.refusalField).length ? { noted: notedIn(d, NOTED_WIRE.refusalField) } : {}),
      ...(Array.isArray(d.records) && d.records.some((r: unknown) => str(r)) ? { records: d.records.filter((r: unknown): r is string => !!str(r)) } : {}),
      ...(typeof d.hidden_records === "number" && d.hidden_records > 0 ? { hiddenRecords: d.hidden_records } : {}),
      status,
    };
  }
  if (str(d.reason)) {
    const conflicts = Array.isArray(d.conflicts) ? d.conflicts : [];
    return { code: str(d.code) ?? "blocked", reason: d.reason, failed: items(conflicts), hidden, next, status };
  }
  return null;
}

/** Any failed call → the one refusal shape. `fallback` is the sentence when
 *  nothing better is known ("The commit failed."). */
export function toRefusal(e: unknown, fallback = "That did not work."): Refusal {
  const status = typeof (e as { status?: unknown })?.status === "number" ? ((e as { status: number }).status) : undefined;
  // an ApiError's body is raw text when the response was not JSON (a proxy's
  // page, a crash): only a `{detail}` the server wrote is its own account
  const body = e instanceof ApiError ? (e.body as { detail?: unknown } | null) : null;
  const detail = e instanceof ApiError
    ? (body && typeof body === "object" && "detail" in body ? body.detail : undefined)
    : (e as { detail?: unknown })?.detail;
  const parsed = detail !== undefined ? fromDetail(detail, status) : null;
  if (parsed) return parsed;
  if (e && typeof e === "object" && !(e instanceof Error)) {
    const direct = fromDetail(e, status);
    if (direct) return direct;
  }
  const message = e instanceof Error ? e.message : str(e);
  // an ApiError's message is "API Error (409): <raw text>": the raw text is
  // the server's, the prefix is noise to a reader
  const cleaned = message?.replace(/^API Error \(\d+\):\s*/, "");
  return { code: "failed", reason: cleaned && cleaned.length < 300 ? `${fallback} ${cleaned}` : fallback, failed: [], next: [], status };
}

/** A chat turn the server ended for a reason (VC-Authz O3): its closing
 *  `system_ui` step carries the refusal as `refusal_json`. */
export function refusalFromStep(step: { refusal_json?: string | null } | null | undefined): Refusal | null {
  const raw = step?.refusal_json;
  if (!raw) return null;
  try {
    return fromDetail(JSON.parse(raw), undefined);
  } catch {
    return null;
  }
}
